import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Empty, Row, Space, Spin, Switch, Tag } from "antd";
import { trainingAPI } from "@api/TrainingAPI";
import { setTeamFilter } from "@store/actions/team.actions";
import { setPlayerFilter } from "@store/actions/player.actions";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { dailyCounts, daysInRange, groupByDay, nearestDay, tableTree, zoneCounts } from "@utils/helmetDerive.mjs";
import { HelmetTiles } from "./HelmetTiles";
import { HelmetDailyChart } from "./HelmetDailyChart";
import { HelmetZonesPanel } from "./HelmetZonesPanel";
import { HelmetImpactTable, dayKey } from "./HelmetImpactTable";
import type { HelmetImpactRow, HelmetLocation } from "./types";
import "./HelmetImpact.css";

const UNKNOWN_PLAYER = "Unknown player";

interface Props {
  showAll?: boolean;
  /** Goes up on each Search click: the tab fetches again and keeps the selected day and the filters. */
  searchCount?: number;
  /** The Player page sets it: the tab shows one player, with the dates of the player filter. */
  playerId?: number;
}

export default function HelmetImpact({ showAll: exportPreview = false, searchCount = 0, playerId }: Props) {
  const dispatch = useDispatch();
  const { filter: teamFilter, allPlayers } = useSelector(({ team }: any) => team);
  const playerFilter = useSelector(({ player }: any) => player.filter);
  // The one place that chooses the filter: the Player page reads the player filter, the Team page the team filter.
  const { startDate, endDate, comparisonOption } = playerId ? playerFilter : teamFilter;
  const setComparisonOption = (value: string) =>
    dispatch(playerId ? setPlayerFilter({ comparisonOption: value }) : setTeamFilter({ comparisonOption: value }));
  const { position, player_group } = teamFilter;
  const [impacts, setImpacts] = useState<HelmetImpactRow[]>([]);
  // True until the first fetch ends, so the empty state does not show before the fetch starts.
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [locations, setLocations] = useState<HelmetLocation[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const requestId = useRef(0);
  // A Search click during a filter-change fetch drops that fetch; its reset must still apply.
  const pendingReset = useRef(false);

  const filterKey = JSON.stringify(playerId ? [startDate, endDate, playerId] : [startDate, endDate, position, player_group]);

  const fetchImpacts = useCallback(
    async (reset: boolean) => {
      if (!startDate || !endDate) return setLoading(false);
      const id = ++requestId.current;
      pendingReset.current ||= reset;
      setLoading(true);
      try {
        const response = await trainingAPI.getHelmetImpacts({
          start_date: startDate,
          end_date: endDate,
          positions: playerId ? [] : position ?? [],
          player_groups: playerId ? [] : player_group ?? [],
          player_id: playerId,
        });
        if (id !== requestId.current) return;
        // The browser filter stays until the backend reads `player_id`.
        const rows: HelmetImpactRow[] = (response?.impacts ?? []).filter((i: HelmetImpactRow) => !playerId || i.player_id === playerId);
        const dayList: string[] = daysInRange(groupByDay(rows), startDate, endDate);
        setImpacts(rows);
        if (pendingReset.current) {
          pendingReset.current = false;
          const latest = dayList.at(-1) ?? null;
          setSelectedDay(latest);
          setExpandedKeys(latest ? [dayKey(latest)] : []);
          setAlertsOnly(false);
          setLocations([]);
        } else {
          setSelectedDay((day) => (day ? nearestDay(dayList, day) : dayList.at(-1) ?? null));
        }
      } catch (e) {
        if (id === requestId.current) {
          message.error("Unable to load the helmet impacts.");
          // The old impacts do not match the new filters.
          if (pendingReset.current) {
            setImpacts([]);
            setSelectedDay(null);
          }
        }
        console.error(e);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [startDate, endDate, position, player_group, playerId],
  );

  useEffect(() => {
    fetchImpacts(true);
  }, [filterKey]);

  const mountSearchCount = useRef(searchCount);
  useEffect(() => {
    if (searchCount !== mountSearchCount.current) fetchImpacts(false);
  }, [searchCount]);

  const days = useMemo(() => groupByDay(impacts), [impacts]);
  const dayList: string[] = useMemo(() => daysInRange(days, startDate, endDate), [days, startDate, endDate]);
  const dayImpacts: HelmetImpactRow[] = (selectedDay && days.get(selectedDay)) || [];
  const counts = useMemo(() => dailyCounts(days, dayList), [days, dayList]);
  const zones = useMemo(() => zoneCounts(dayImpacts), [dayImpacts]);
  const tree = useMemo(
    () => tableTree(impacts, { startDate, endDate, alertsOnly, locations }),
    [impacts, startDate, endDate, alertsOnly, locations],
  );

  const playerNames = useMemo(
    () =>
      new Map<number, string>(
        (allPlayers ?? []).map((p: any) => [p.player_id, `${p.first_name?.[0] ?? ""}. ${p.last_name ?? ""}`.trim()]),
      ),
    [allPlayers],
  );
  const playerName = useCallback((id: number) => playerNames.get(id) ?? UNKNOWN_PLAYER, [playerNames]);

  const toggleLocation = (location: HelmetLocation) =>
    setLocations((current) => (current.includes(location) ? current.filter((l) => l !== location) : [...current, location]));

  return (
    // Spin with `spinning` keeps the chart, the 3D map and the table mounted during a refetch.
    <Spin spinning={loading} tip="Loading helmet impacts..." size="large">
      <div className="helmet-impact">
        <div className="helmet-export-tiles">
          <HelmetTiles
            playerId={playerId}
            startDate={startDate}
            endDate={endDate}
            comparisonOption={comparisonOption}
            setComparisonOption={setComparisonOption}
          />
        </div>

        {!loading && !dayList.length ? (
          <Empty description="No helmet impacts in this date range" />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={15} className="helmet-export">
                <HelmetDailyChart counts={counts} selectedDay={selectedDay} onSelectDay={setSelectedDay} />
              </Col>
              <Col xs={24} lg={9} className="helmet-export">
                <HelmetZonesPanel
                  zones={zones}
                  selectedDay={selectedDay}
                  selectedLocations={locations}
                  onToggleLocation={toggleLocation}
                  exportPreview={exportPreview}
                />
              </Col>
            </Row>

            <div>
              <div className="helmet-table-toolbar">
                <span className="helmet-panel-title" style={{ marginBottom: 0 }}>Impacts by Day</span>
                <Switch checked={alertsOnly} onChange={setAlertsOnly} checkedChildren="Alerts only" unCheckedChildren="Alerts only" />
                <Space style={{ marginLeft: "auto" }} wrap>
                  {locations.map((location) => (
                    <Tag key={location} closable onClose={() => toggleLocation(location)}>
                      Location: {location}
                    </Tag>
                  ))}
                  {locations.length > 1 && (
                    <Button type="link" size="small" onClick={() => setLocations([])}>
                      Clear
                    </Button>
                  )}
                </Space>
              </div>
              <HelmetImpactTable
                tree={tree}
                expandedKeys={expandedKeys}
                onExpandedKeysChange={setExpandedKeys}
                playerName={playerName}
                singlePlayer={!!playerId}
                exportPreview={exportPreview}
              />
            </div>
          </>
        )}
      </div>
    </Spin>
  );
}
